import { FC, useState } from "react";
import {
  PortalContainerProvider,
  Sheet,
  SheetContent,
} from "@capacit-com/lara-law-shared-ui";
import useDashboardStore from "@/stores/dashboard.store";
import WidgetGallery, { GalleryPick } from "./WidgetGallery";
import WidgetEditor from "./WidgetEditor";

interface WidgetEditorPanelProps {
  /** The in-flow slot the panel renders into: beside the grid, or below it on a narrow screen. */
  container: HTMLElement;
  /** Close the panel, after the parent's guard for unapplied input. */
  onRequestClose: () => void;
  onDraftStateChange: (hasDraft: boolean) => void;
}

/**
 * Overrides for SheetContent's fixed overlay classes. The panel sits in the
 * page's flow (LAR-726): the grid shrinks beside it and stays scrollable, and
 * below `md` the panel stacks under the grid.
 */
const IN_FLOW_SHEET_CLASSES =
  "relative z-auto h-full max-h-none overflow-visible shadow-none data-[side=right]:inset-y-auto data-[side=right]:right-auto data-[side=right]:h-full data-[side=right]:w-full data-[side=right]:border-l-0 data-[side=right]:sm:max-w-none";

/**
 * "Tilføj widget" and "Rediger widget" in one right-side panel (LAR-726). It is
 * non-modal — no overlay, no focus trap — so the grid behind it stays live, and
 * only the Kilder and Workspace pickers open as modals on top of it.
 */
const WidgetEditorPanel: FC<WidgetEditorPanelProps> = ({
  container,
  onRequestClose,
  onDraftStateChange,
}) => {
  const isGalleryOpen = useDashboardStore((s) => s.isWidgetGalleryOpen);
  const editingWidget = useDashboardStore((s) =>
    s.localConfig?.widgets.find((w) => w.id === s.editingWidgetId)
  );
  const addWidget = useDashboardStore((s) => s.addWidget);
  const beginWidgetEditing = useDashboardStore((s) => s.beginWidgetEditing);

  // The widget just placed from the gallery, whose default name the editor selects.
  const [addedWidgetId, setAddedWidgetId] = useState<string | null>(null);

  const isOpen = isGalleryOpen || editingWidget !== undefined;

  const handlePick = ({ type, name, config, size }: GalleryPick) => {
    const id = addWidget(type, name, config, size);
    if (!id) return;
    setAddedWidgetId(id);
    beginWidgetEditing(id);
  };

  return (
    // The panel itself portals into the slot; everything inside it (selects,
    // tooltips, pickers) portals to the body as usual.
    <PortalContainerProvider container={container}>
      <Sheet open={isOpen} modal={false}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className={IN_FLOW_SHEET_CLASSES}
          id="widget-editor-panel"
          // The grid is meant to be used while the panel is open: clicking or
          // tabbing to it, or opening a widget's menu, never closes the panel.
          onInteractOutside={(event) => event.preventDefault()}
          // Each view moves focus to its own first field.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            // A field that uses Esc itself (cancelling an item rename) keeps it.
            if (
              event.target instanceof Element &&
              event.target.closest("[data-escape-local]")
            )
              return;
            onRequestClose();
          }}
        >
          <PortalContainerProvider container={undefined}>
            {isGalleryOpen ? (
              <WidgetGallery onPick={handlePick} onClose={onRequestClose} />
            ) : (
              editingWidget && (
                <WidgetEditor
                  key={editingWidget.id}
                  widget={editingWidget}
                  isNew={editingWidget.id === addedWidgetId}
                  onClose={onRequestClose}
                  onDraftStateChange={onDraftStateChange}
                />
              )
            )}
          </PortalContainerProvider>
        </SheetContent>
      </Sheet>
    </PortalContainerProvider>
  );
};

export default WidgetEditorPanel;
